import { useEffect, useState } from "react";
import { ScatterChart } from "@mui/x-charts/ScatterChart";
import { Card, CardContent, Typography } from "@mui/material";
import apiService from "../../../../../../service/Api";

/* =====================
   TYPES
===================== */
interface UploadSpeedVsCapacityItem {
  venue_id: number;
  venue_name: string;
  upload_speed: number;
  capacity: string;
}

interface UploadSpeedVsCapacityResponse {
  data: UploadSpeedVsCapacityItem[];
  total_count: number;
  title: string;
}

/* =====================
   COMPONENT
===================== */
const CapacitySpeedQuality = () => {
  const [data, setData] = useState<
    { x: number; y: number; z?: number; label: string }[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await apiService.get<UploadSpeedVsCapacityResponse>(
          "/api/venues/dashboard/upload-speed-vs-capacity/"
        );

        const items = res.data?.data ?? [];

        const mapped = items
          .map((item) => {
            const capacityNum = Number(item.capacity);
            if (isNaN(capacityNum)) return null;

            return {
              x: capacityNum,
              y: item.upload_speed,
              z: capacityNum, // bubble size proportional to capacity
              label: item.venue_name, // tooltip shows this automatically
            };
          })
          .filter(Boolean) as {
          x: number;
          y: number;
          z?: number;
          label: string;
        }[];

        setData(mapped);
      } catch (error) {
        console.error("Failed to fetch upload speed vs capacity data", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  if (loading) return <Typography>Loading...</Typography>;
  if (!data.length) return <Typography>No valid data available</Typography>;

  return (
    <Card sx={{ height: "100%", width: "100%" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Upload Speed vs Capacity
        </Typography>

        <ScatterChart
          height={300}
          series={[
            {
              data: data,
              // Each point has a label, which is automatically used in the tooltip
            },
          ]}
          xAxis={[
            {
              label: "Capacity",
              valueFormatter: (value: number) => value.toString(), // no commas
            },
          ]}
          yAxis={[
            {
              label: "Upload Speed (Mbps)",
            },
          ]}
        />
      </CardContent>
    </Card>
  );
};

export default CapacitySpeedQuality;
